import React, { useEffect } from 'react';

interface GoogleAnalyticsProps {
  trackingId?: string;
  tagManagerId?: string;
}

const GoogleAnalytics: React.FC<GoogleAnalyticsProps> = ({ 
  trackingId = 'G-78VZ8YXZCC',
  tagManagerId
}) => {
  useEffect(() => {
    // Ultra-deferred Google Analytics loading to reduce unused JavaScript
    const loadAnalytics = () => {
      if (!trackingId || trackingId === 'G-XXXXXXXXXX') return;
      
      // Load only after user interaction or significant delay
      const loadGoogleAnalytics = () => {
        // Check if already loaded
        if (window.gtag) return;
        
        // Create script with async loading
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
        script.onload = () => {
          window.dataLayer = window.dataLayer || [];
          function gtag(...args: any[]){ window.dataLayer.push(args); }
          gtag('js', new Date());
          gtag('config', trackingId, {
            page_title: document.title,
            page_location: window.location.href
          });
          window.gtag = gtag;
        };
        document.head.appendChild(script);
      };
      
      // Load on user interaction for better performance
      const loadOnInteraction = () => {
        loadGoogleAnalytics();
        // Remove listeners
        document.removeEventListener('scroll', loadOnInteraction);
        document.removeEventListener('click', loadOnInteraction);
        document.removeEventListener('touchstart', loadOnInteraction);
      };
      
      // Listen for user interactions
      document.addEventListener('scroll', loadOnInteraction, { once: true, passive: true });
      document.addEventListener('click', loadOnInteraction, { once: true, passive: true });
      document.addEventListener('touchstart', loadOnInteraction, { once: true, passive: true });
      
      // Fallback: load after 5 seconds if no interaction
      setTimeout(loadGoogleAnalytics, 5000);
    };
    
    // Use requestIdleCallback for even better performance
    if ('requestIdleCallback' in window) {
      requestIdleCallback(loadAnalytics, { timeout: 2000 });
    } else {
      setTimeout(loadAnalytics, 1000);
    }
  }, [trackingId]);

  // GTM is also deferred
  useEffect(() => {
    if (!tagManagerId || tagManagerId === 'GTM-XXXXXXX') return;
    
    const loadGTM = () => {
      if (window.dataLayer) return; // Already loaded
      
      const script = document.createElement('script');
      script.innerHTML = `
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${tagManagerId}');
      `;
      document.head.appendChild(script);
    };
    
    // Even more deferred GTM loading
    setTimeout(loadGTM, 3000);
  }, [tagManagerId]);

  return null; // No immediate rendering
};

// Hook para tracking de eventos
export const useAnalytics = () => {
  const trackEvent = (eventName: string, parameters: Record<string, any> = {}) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, parameters);
    }
  };

  const trackPageView = (page_title: string, page_location: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'GA_TRACKING_ID', {
        page_title,
        page_location
      });
    }
  };

  return { trackEvent, trackPageView };
};

// Declaración de tipos para gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: Record<string, any>[];
  }
}

export default GoogleAnalytics;